// src/components/ui/CommentSection.jsx
import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button 
} from '@mui/material';
import { AddComment as CommentIcon } from '@mui/icons-material';
import { Card } from '../../design-system';
import { Avatar, useTheme, alpha } from '@mui/material';
import { format } from 'date-fns';
import MentionText from './MentionText';
import InlineMentionInput from './FormFields/InlineMentionInput';

/**
 * Component for displaying a single comment in a list
 * @param {Object} props
 * @param {Object} props.comment - The comment object
 * @param {string} props.currentUserId - The ID of the current user
 */
const CommentItem = ({ comment, currentUserId }) => {
  const theme = useTheme();
  
  // Format date
  const formatCommentDate = (date) => {
    return format(new Date(date), 'MMM d, yyyy HH:mm');
  };
  
  const isCurrentUserComment = comment.user?._id === currentUserId;
  
  return (
    <Box sx={{ 
      mb: 2, 
      p: 2, 
      borderRadius: 1,
      bgcolor: isCurrentUserComment 
        ? alpha(theme.palette.primary.main, 0.05) 
        : alpha(theme.palette.grey[100], 0.5)
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Avatar 
          sx={{ 
            width: 32, 
            height: 32, 
            bgcolor: isCurrentUserComment 
              ? theme.palette.primary.main 
              : theme.palette.grey[500]
          }}
        >
          {comment.user?.name?.charAt(0) || 'U'}
        </Avatar>
        <Box sx={{ ml: 1 }}>
          <Typography variant="subtitle2">
            {comment.user?.name || 'User'}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {formatCommentDate(comment?.timestamp || comment?.createdAt)}
          </Typography>
        </Box>
      </Box>
      <MentionText 
        text={comment.content || comment.text}
        typographyProps={{ variant: "body2" }}
        onMentionClick={(user) => console.log('Mention clicked:', user)}
      />
    </Box>
  );
};

/**
 * Reusable comment section component for content
 * @param {Object} props
 * @param {Array} props.comments - Array of comment objects
 * @param {Function} props.onAddComment - Function to call when adding a comment
 * @param {string} props.currentUserId - ID of the current user
 */
const CommentSection = ({ comments = [], onAddComment, currentUserId }) => {
  const [commentText, setCommentText] = useState('');
  const [mentionedUsers, setMentionedUsers] = useState([]);
  
  const handleSubmit = () => {
    if (!commentText.trim()) return;
    // Pass both the comment text and the mentioned users
    onAddComment(commentText, mentionedUsers);
    setCommentText('');
    setMentionedUsers([]);
  };
  
  const handleCommentChange = (newValue) => {
    setCommentText(newValue);
  };
  
  const handleMentionAdd = (user) => {
    // Add user to mentionedUsers if not already there
    if (!mentionedUsers.some(u => u._id === user._id)) {
      setMentionedUsers([...mentionedUsers, user]);
    }
  };
  
  const handleMentionRemove = (userId) => {
    setMentionedUsers(mentionedUsers.filter(user => user._id !== userId));
  };
  
  return (
    <Box>
      {/* Add comment */}
      <Card level={1} sx={{ mb: 3 }}>
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Add Comment
          </Typography>
          <Box sx={{ mb: 2 }}>
            <InlineMentionInput
              value={commentText}
              onChange={handleCommentChange}
              onMentionAdd={handleMentionAdd}
              onMentionRemove={handleMentionRemove}
              placeholder="Write your comment here... Use @ to mention users"
              inputProps={{
                style: { minHeight: '100px' }
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <Button
              variant="contained"
              startIcon={<CommentIcon />}
              onClick={handleSubmit}
              disabled={!commentText.trim()}
            >
              Add Comment
            </Button>
          </Box>
        </Box>
      </Card>
      
      {/* Comments list */}
      <Box>
        <Typography variant="h6" gutterBottom>
          Comments ({comments.length || 0})
        </Typography>
        
        {!comments || comments.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1" color="textSecondary">
              No comments yet
            </Typography>
          </Paper>
        ) : (
          <Box>
            {comments.map((comment, index) => (
              <CommentItem 
                key={comment._id || index} 
                comment={comment} 
                currentUserId={currentUserId}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CommentSection;